import React, {useEffect, useRef, useState} from 'react';
import {Box} from '@mui/material';
import type {TextChangesProps} from 'components/text/types';
import type {Browser} from 'services/utils';
import {useIsBrowser} from 'services/utils';
import {Layout} from 'components/layout';
import {TextChanges} from 'components/text/TextChanges';
import type {TextFieldProps} from './types';
import {FieldLabel} from './FieldLabel';
import {MuiTextFieldStyled} from './muiStyled';

type TextFieldWithChangesProps = Omit<TextFieldProps, 'slotProps'> & {
  slotProps: Omit<TextFieldProps['slotProps'], 'input'> & {
    input: {
      id: string;
      startAdornment?: React.ReactNode;
      endAdornment?: React.ReactNode;
    };
  };
  className?: string;
};

const browsersWithInputScroll: Browser[] = ['chrome', 'firefox', 'edge'];

/**
 * Variant of TextField that shows changes when in diff mode, otherwise null
 * Should not be used directly, but through TextField
 */
export const TextFieldWithChanges: React.FC<TextFieldWithChangesProps> = ({
  label,
  placeholder,
  name,
  type,
  required = false,
  readonly = false,
  value,
  className,
  diff,
  error = false,
  onFocus,
  onBlur,
  layout,
  slotProps,
  ...props
}) => {
  const hasInputScroll = useIsBrowser(browsersWithInputScroll);
  const [hasFocus, setHasFocus] = useState(false);

  const textChangesProps: TextChangesProps | undefined = diff
    ? {
        original:
          diff.mode === 'original' ? (value as string) : diff.compareValue,
        updated:
          diff.mode === 'original' ? diff.compareValue : (value as string),
        diffMode: diff.mode,
      }
    : undefined;

  const inputRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const overlayContainerRef = useRef<HTMLElement>(null);
  const [offset, setOffset] = useState({top: 0, left: 0});

  useEffect(() => {
    const calculatePosition = () => {
      if (inputRef.current && containerRef.current) {
        const inputRect = inputRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();

        const relativeTop = inputRect.top - containerRect.top;
        const relativeLeft = inputRect.left - containerRect.left;

        setOffset({top: relativeTop, left: relativeLeft});
      }
    };

    calculatePosition();

    if (!hasInputScroll) {
      return;
    }

    const scrollListener = () => {
      overlayContainerRef.current?.scroll(inputRef.current?.scrollLeft ?? 0, 0);
    };
    const element = inputRef.current;

    element?.addEventListener('scroll', scrollListener);

    return () => {
      element?.removeEventListener('scroll', scrollListener);
    };
  }, [hasInputScroll]);

  return (
    <Layout {...layout}>
      {label && (
        <FieldLabel
          label={label}
          required={required}
          error={error}
          htmlFor={slotProps.input.id}
        />
      )}
      {textChangesProps ? (
        <Box
          ref={containerRef}
          sx={{
            position: 'relative',
          }}>
          <MuiTextFieldStyled
            required={required !== false}
            size="small"
            fullWidth
            id={name}
            name={name}
            placeholder={placeholder}
            value={(value ?? '') as string | number}
            readonly={readonly}
            type={type}
            error={error === true}
            color={
              error === 'warning' ? 'warning' : error ? 'error' : undefined
            }
            slotProps={{
              ...slotProps,
              input: {
                ...slotProps.input,
                style: {overflow: 'hidden'},
                inputRef: inputRef,
              },
            }}
            onFocus={e => {
              setHasFocus(true);
              onFocus?.(e);
            }}
            onBlur={e => {
              setHasFocus(false);
              onBlur?.(e);
            }}
            {...props}
            className={className}></MuiTextFieldStyled>
          {hasInputScroll || !hasFocus ? (
            <Box
              sx={[
                {
                  position: 'absolute',
                  width: '100%',
                  py: 1,
                  pointerEvents: 'none',
                  color: 'transparent',
                  ...offset,
                },
              ]}>
              <Box
                ref={overlayContainerRef}
                sx={{
                  textWrap: 'nowrap',
                  overflow: 'hidden',
                  marginRight: `${offset.left}px`,
                  paddingLeft: 1.75,
                }}>
                <TextChanges {...textChangesProps} />
              </Box>
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Layout>
  );
};
